import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import { z, ZodObject, ZodIntersection } from 'zod'
import { reportError } from '@mc/next-newrelic'
import { TrackOptions, useSegmentTracking } from '@mc/tracking-context'
import { TrackingContainer, VisibilityConfig } from '@mc/visibility'
import { postToXRay } from '@mc/x-ray'

type TrackingSchema = Record<
  string,
  {
    eventName: string
    schema: z.ZodType<Record<string, unknown>>
    options?: TrackOptions
  }
>

export const createTracking = <
  T extends TrackingSchema,
  P extends z.ZodType<{ name: string } & Record<string, unknown>>,
>(
  schema: T,
  pageSchema: P,
) => {
  type SchemaType = {
    [K in keyof T]: z.infer<T[K]['schema']>
  }
  type PageAttributes = z.infer<P>

  // Extract event names from the schema object keys
  type EventNames = keyof SchemaType

  // Extract the property types for a specific event
  type EventAttributes<K extends EventNames> = Partial<SchemaType[K]>

  // Helper type to extract all keys from a union of objects
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type AllKeysFromUnion<U> = U extends any ? keyof U : never

  // Extract all possible property keys from all event schemas
  type AllEventKeys = AllKeysFromUnion<SchemaType[EventNames]>

  // Helper type to extract the correct type for a key from a union of objects
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type ExtractKeyType<U, K extends PropertyKey> = U extends any
    ? K extends keyof U
      ? U[K]
      : never
    : never

  // Create a type that allows any combination of properties from all event schemas
  // with their correct types
  type EventAttributesType = Partial<{
    [K in AllEventKeys]: ExtractKeyType<SchemaType[EventNames], K>
  }>

  // Create a type that includes both event and page attributes
  type ValidAttributes = EventAttributesType & Partial<PageAttributes>

  // Create a unique context for this tracking instance
  const TrackingContext = createContext<ValidAttributes>({})

  const TrackingAttributes = ({
    children,
    ...attributes
  }: {
    children: ReactNode
  } & ValidAttributes) => {
    const parent = useContext(TrackingContext)
    const merged = useMemo(
      () => ({ ...parent, ...attributes }),
      [parent, attributes],
    )

    return (
      <TrackingContext.Provider value={merged}>
        {children}
      </TrackingContext.Provider>
    )
  }

  const useTrackingAttributes = () => useContext(TrackingContext)

  const validateAndExtractPayload = <K extends EventNames>(
    eventName: K,
    contextAttributes: ValidAttributes,
    localAttributes: ValidAttributes,
  ) => {
    // Get the specific schema for this event
    const eventConfig = schema[eventName as keyof typeof schema]

    if (!eventConfig) {
      const error = `Event schema not found for: ${String(eventName)}`
      postToXRay('@mc/tracking-kit', 'error', { eventName, message: error })
      reportError(new Error(error), {
        eventName: String(eventName),
        availableEvents: JSON.stringify(Object.keys(schema)),
      })
      return { error: { message: error } }
    }

    const allAttributes = {
      ...contextAttributes,
      ...localAttributes,
    }
    const result = eventConfig.schema.safeParse(allAttributes)

    if (!result.success) {
      reportError(
        new Error(`Tracking validation failed: ${result.error.message}`),
        {
          eventName: String(eventName),
          contextAttributes: JSON.stringify(contextAttributes),
          localAttributes: JSON.stringify(localAttributes),
          validationErrors: JSON.stringify(result.error.format()),
        },
      )

      // If parsing fails, we still want to fire the event; partial data is better than no data
      return {
        properties: {
          ...allAttributes,
          eventName: eventConfig.eventName,
          options: eventConfig.options,
        },
        error: {
          message: `Tracking validation failed for ${String(eventName)}`,
          errors: result.error.format(),
        },
      }
    }

    return {
      properties: {
        ...result.data,
        eventName: eventConfig.eventName,
        options: eventConfig.options,
      },
    }
  }

  const useFireEvent = () => {
    const contextAttributes = useTrackingAttributes()
    const { track } = useSegmentTracking()

    // Store current values in refs so they can be accessed without being dependencies
    const contextAttributesRef = useRef(contextAttributes)
    const trackRef = useRef(track)

    // Update refs on every render to access current values
    contextAttributesRef.current = contextAttributes
    trackRef.current = track

    const fireEvent = useCallback(
      <K extends EventNames>(
        eventName: K,
        localAttributes: ValidAttributes = {},
      ) => {
        const { properties, error } = validateAndExtractPayload(
          eventName,
          contextAttributesRef.current,
          localAttributes,
        )

        let augmentedProps: Record<string, unknown> | undefined
        if (properties) {
          const { eventName: segmentEventName, options, ...rest } = properties
          augmentedProps = trackRef.current(
            segmentEventName,
            rest as Record<
              string,
              string | number | boolean | object | null | undefined
            >,
            options,
          )
        }

        if (error) {
          postToXRay('@mc/tracking-kit', 'error', {
            ...error,
            eventName: properties?.eventName ?? eventName,
            eventId: augmentedProps?.event_id,
          })
        }
      },
      [],
    ) // Empty dependency array makes this function stable

    return fireEvent
  }

  // Helper function to extract text content from React children
  const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string') {
      return children.trim()
    }

    if (typeof children === 'number') {
      return String(children).trim()
    }

    if (React.isValidElement(children)) {
      return extractTextFromChildren(children.props.children)
    }

    if (Array.isArray(children)) {
      return children
        .map(extractTextFromChildren)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
    }

    return ''
  }

  const TrackEvent = <K extends EventNames>({
    event,
    children,
    trigger = 'onClick',
    enabled = true,
    onClick,
    onMouseEnter,
    onFocus,
    onBlur,
    onDoubleClick,
    onSubmit,
    onClose,
    ...attributes
  }: {
    event: K
    children: React.ReactElement
    trigger?:
      | 'onClick'
      | 'onMouseEnter'
      | 'onFocus'
      | 'onBlur'
      | 'onDoubleClick'
      | 'onSubmit'
      | 'onClose'
    enabled?: boolean
    onClick?: (...args: unknown[]) => void
    onMouseEnter?: (...args: unknown[]) => void
    onFocus?: (...args: unknown[]) => void
    onBlur?: (...args: unknown[]) => void
    onDoubleClick?: (...args: unknown[]) => void
    onSubmit?: (...args: unknown[]) => void
    onClose?: (...args: unknown[]) => void
  } & EventAttributes<K>) => {
    const fire = useFireEvent()

    // Get the existing handler from the child component
    const existingHandler = children.props?.[trigger]

    // Get the any handlers added directly to this component
    const selfHandler = {
      onClick,
      onMouseEnter,
      onFocus,
      onBlur,
      onDoubleClick,
      onSubmit,
      onClose,
    }[trigger]

    const createComposedHandler =
      (originalHandler?: (...args: unknown[]) => void) =>
      (...args: unknown[]) => {
        // Call passed-in handler first (from parent TrackEvent)
        if (selfHandler) {
          selfHandler(...args)
        }

        // Only fire tracking event if enabled
        if (enabled) {
          // Fire our tracking event
          fire(event, attributes as unknown as ValidAttributes)
        }

        // Then call the original handler if it exists (from child element)
        if (originalHandler) {
          originalHandler(...args)
        }
      }

    // Ensure we have a valid element to clone
    if (!React.isValidElement(children)) {
      return children
    }

    return React.cloneElement(children, {
      [trigger]: createComposedHandler(existingHandler),
    })
  }

  const schemaHasAction = (zodSchema: z.ZodTypeAny): boolean => {
    // Check if it's a ZodObject with a shape containing 'action'
    if (zodSchema instanceof ZodObject) {
      return 'action' in zodSchema.shape
    }

    // Check if it's a ZodIntersection (created by .and())
    if (zodSchema instanceof ZodIntersection) {
      const intersectionDef = zodSchema._def as {
        left: z.ZodTypeAny
        right: z.ZodTypeAny
      }
      return (
        schemaHasAction(intersectionDef.left) ||
        schemaHasAction(intersectionDef.right)
      )
    }

    return false
  }

  const TrackClick = <K extends EventNames>({
    event,
    children,
    enabled = true,
    autoAction = true,
    onClick,
    ...attributes
  }: {
    event: K
    children: React.ReactElement
    enabled?: boolean
    autoAction?: boolean
    onClick?: (...args: unknown[]) => void
  } & EventAttributes<K>) => {
    // Check if the event schema includes 'action'
    const eventConfig = schema[event as keyof typeof schema]
    const hasActionInSchema = eventConfig && schemaHasAction(eventConfig.schema)

    // If autoAction is enabled, no explicit action is provided, and action is in the schema
    const shouldExtractAction =
      autoAction &&
      hasActionInSchema &&
      !('action' in attributes && attributes.action !== undefined)

    const extractedAction = shouldExtractAction
      ? extractTextFromChildren(children.props?.children)
      : undefined
    return (
      <TrackEvent
        event={event}
        trigger='onClick'
        enabled={enabled}
        onClick={onClick}
        {...(extractedAction ? { action: extractedAction } : {})}
        {...attributes}
      >
        {children}
      </TrackEvent>
    )
  }

  const TrackPageViewed = ({
    options,
    ...attributes
  }: Partial<PageAttributes> & { options?: TrackOptions }) => {
    const { trackPage } = useSegmentTracking()
    const contextAttributes = useTrackingAttributes()

    useEffect(() => {
      // Merge context attributes with local attributes (local takes precedence)
      const mergedAttributes = { ...contextAttributes, ...attributes }

      const result = pageSchema.safeParse(mergedAttributes)

      if (!result.success) {
        postToXRay('@mc/tracking-kit', 'error', {
          eventName: 'Page Loaded',
          message: 'Page tracking validation failed',
          errors: result.error.format(),
          attributes: mergedAttributes,
        })

        reportError(
          new Error(`Page tracking validation failed: ${result.error.message}`),
          {
            attributes: JSON.stringify(mergedAttributes),
            validationErrors: JSON.stringify(result.error.format()),
          },
        )
        return
      }

      const properties = result.data
      trackPage(
        result.data.name as string,
        properties as Record<
          string,
          string | number | boolean | object | null | undefined
        >,
        options,
      )

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return null
  }

  const TrackOnMount = <K extends EventNames>({
    event,
    enabled = true,
    ...attributes
  }: {
    event: K
    enabled?: boolean
  } & EventAttributes<K>) => {
    const fire = useFireEvent()
    const hasTracked = useRef(false)

    useEffect(() => {
      if (!enabled || hasTracked.current) {
        return
      }

      fire(event, attributes as unknown as ValidAttributes)
      hasTracked.current = true
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled])

    return null
  }

  const TrackVisible = <K extends EventNames>({
    event,
    children,
    visibilityConfig,
    className,
    ...attributes
  }: {
    event: K
    children: React.ReactElement
    visibilityConfig?: VisibilityConfig
    className?: string
  } & EventAttributes<K>) => {
    const fire = useFireEvent()

    const handleVisible = () => {
      fire(event, attributes as unknown as ValidAttributes)
    }

    return (
      <TrackingContainer
        onFirstVisible={handleVisible}
        config={visibilityConfig}
        className={className}
      >
        {children}
      </TrackingContainer>
    )
  }

  const TrackExperimentViewed = ({
    name: experimentName,
    enabled = true,
    ...attributes
  }: {
    name: string
    enabled?: boolean
  } & Record<string, string | number | boolean | null | undefined>) => {
    const { trackExperimentViewed } = useSegmentTracking()

    useEffect(() => {
      if (enabled) {
        trackExperimentViewed(experimentName, attributes)
      }
    }, [experimentName, trackExperimentViewed, attributes, enabled])

    return null
  }

  return {
    useFireEvent,
    TrackEvent,
    TrackClick,
    TrackExperimentViewed,
    TrackPageViewed,
    TrackOnMount,
    TrackVisible,
    TrackingAttributes,
    useTrackingAttributes,
  }
}
